import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Checkbox,
  HighlightedText,
  IOption,
  MultiSelect
} from "@digital-services/ui";

import ErrorComponent from "@components/ErrorComponent";
import LoadingComponent from "@components/LoadingComponent";

import { useGetApplicationRoles } from "@users/applicationuser/services/ApplicationUserService";

import "./editApplicationAssociation.css";

function uniqueFilter(value: IOption, index: number, self: IOption[]) {
  return self.findIndex(item => item.value === value.value) === index;
}

const prepareOptionsFrom = (actualRoles: string[]): IOption[] => {
  const rolesOptions: IOption[] = [];

  if (actualRoles.length > 0) {
    actualRoles.forEach(role => {
      rolesOptions.push({ label: role, value: role });
    });
  }

  return rolesOptions;
};

interface Props {
  applicationId: string;
  applicationName: string;
  appEnv?: string;
  isApplicationEnabled: boolean;
  userAppAssocError?: null | string;
  isDisabled?: boolean;
  userRoles: string[];
  onAppRoleChange: (
    applicationId: string,
    applicationName: string,
    applicationRoles: string[],
    isValid: boolean
  ) => void;
  onAppRemove: () => void;
}

export function EditApplicationAssociation(props: Readonly<Props>) {
  const {
    applicationId,
    applicationName,
    appEnv,
    isApplicationEnabled,
    userAppAssocError,
    isDisabled = false,
    userRoles,
    onAppRoleChange,
    onAppRemove
  } = props;

  // Problem of this component that it depends on PARENT and SIBLING components, and checkbox check cause RE-RENDERING ALL - to be refactored in future

  // For Organizations \ edit User
  // Parent component iterates list of applications and then render this component.
  // But ANYWAY the whole cycle renders THREE times !!!

  // For Application \ edit User
  // this component renders SIX times !!!
  // Click of Multiselect item inside of this component causes FOUR TIMES render of this component

  const { t } = useTranslation(undefined, {
    keyPrefix: "user.applications"
  });
  const { t: tApplicationRoles } = useTranslation(undefined, {
    keyPrefix: "application.roles"
  });

  const [options, setOptions] = useState<IOption[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<IOption[]>([]);
  const [isValid, setIsValid] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const [isChecked, setIsChecked] = useState<boolean>(isApplicationEnabled);

  const { applicationRoles, limit, isLoading, error } =
    useGetApplicationRoles(applicationId);

  const setValidState = useCallback(
    (
      isChecked: boolean,
      selectedOptions: IOption[],
      limit: number,
      appRoles: string[]
    ): boolean => {
      if (!isChecked) {
        setIsValid(true);
        setErrorMessage(undefined);

        return true;
      }

      if (limit > -1 && selectedOptions.length > limit) {
        setIsValid(false);
        const errorMessage = t("role.validation.max-roles", { limit: limit });
        setErrorMessage(errorMessage);

        return false;
      }

      if (
        selectedOptions.length &&
        appRoles.length &&
        selectedOptions.every(val => !appRoles.includes(val.value as string))
      ) {
        setIsValid(false);
        const errorMessage = t("role.validation.incompatible");
        setErrorMessage(errorMessage);

        return false;
      }

      setIsValid(true);
      setErrorMessage(undefined);

      return true;
    },
    [t]
  );

  // Map applicationRoles to options of all roles
  useEffect(() => {
    setOptions(prepareOptionsFrom(applicationRoles));
  }, [applicationRoles]);

  // Map userRoles to selected only options
  useEffect(() => {
    if (!isChecked) {
      setSelectedOptions([]);
    }

    if (userRoles.length > 0) {
      setSelectedOptions(prepareOptionsFrom(userRoles));
    }
  }, [isChecked, userRoles]);

  useEffect(() => {
    setValidState(isChecked, selectedOptions, limit, applicationRoles);
  }, [isChecked, selectedOptions, limit, setValidState, applicationRoles]);

  const handleAppRoleChange = (options: IOption[] | null) => {
    options = options ?? [];

    const uniqueOptions = options.filter((v, i, s) => uniqueFilter(v, i, s));
    setSelectedOptions(uniqueOptions);

    const isValid = setValidState(isChecked, options, limit, applicationRoles);

    if (options.length === 0) {
      onAppRoleChange(applicationId, applicationName, [], isValid);
      return;
    }
    if (limit > -1 && options.length > limit) {
      onAppRoleChange(
        applicationId,
        applicationName,
        options.map(item => item.value) as string[],
        isValid
      );
      return;
    }

    onAppRoleChange(
      applicationId,
      applicationName,
      options.map(item => item.value) as string[],
      isValid
    );
  };

  const handleCheckChange = (event: ChangeEvent<HTMLInputElement>) => {
    const mainCheckboxChecked = event.target.checked;

    setIsChecked(mainCheckboxChecked);

    const roles = mainCheckboxChecked ? prepareOptionsFrom(userRoles) : [];
    setSelectedOptions(roles);

    const isValid = setValidState(
      mainCheckboxChecked,
      roles,
      limit,
      applicationRoles
    );

    if (!mainCheckboxChecked) onAppRemove();

    if (mainCheckboxChecked) {
      onAppRoleChange(
        applicationId,
        applicationName,
        roles.map(option => option.value.toString()),
        isValid
      );
    }
  };

  if (isLoading)
    return <LoadingComponent message={tApplicationRoles("load.message")} />;
  if (error)
    return <ErrorComponent message={tApplicationRoles("load.error.message")} />;

  const disabledCheckbox =
    (limit !== 0 && applicationRoles.length === 0) || isDisabled;

  let labelValue = applicationName;

  if (appEnv) {
    labelValue += ` (${appEnv})`;
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="self-left">
        <Checkbox
          id={applicationId}
          name={`edit-application-${applicationId}`}
          data-testid={`edit-user-checkbox-${applicationId}`}
          label={labelValue}
          checked={isChecked}
          variant="alternative"
          disabled={disabledCheckbox}
          onChange={handleCheckChange}
        />
      </div>
      {limit !== 0 && applicationRoles.length === 0 && (
        <span>{tApplicationRoles("load.none.message")}</span>
      )}
      {limit !== 0 && applicationRoles.length > 0 && (
        <div data-testid={`edit-associated-applications-${applicationId}`}>
          <MultiSelect
            placeholder={tApplicationRoles("placeholder")}
            disabled={!isChecked || isDisabled}
            error={!isValid && isChecked}
            helperText={isValid ? undefined : errorMessage}
            options={options}
            // TBD fix UI select roles BUG
            selected={selectedOptions.length === 0 ? null : selectedOptions}
            onChange={el => {
              // MultiSelect issue when unselecting option triggers 2 times the same value.
              // Issue appears only once on initialization of component
              if (el && el.length > 1 && el[0].value === el[1].value) {
                handleAppRoleChange([]);
              } else {
                handleAppRoleChange(el);
              }
            }}
          />
        </div>
      )}
      {userAppAssocError ? (
        <span className="text-xs">
          <HighlightedText
            text={t("association-warning")}
            highlighted="error"
          />
        </span>
      ) : null}
    </div>
  );
}
